import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createModal } from "react-modal-promise";
import pencil from "../../assets/pencil.png";
import trash from "../../assets/trash.png";
import { moduleListService } from "../../services/module-list.service";
import LogicContext from "../../Context/LogicContext";
import GeneralModal from "../../helpers/GeneralModal";
import { SetChildMenuEdit } from "../../store/childMenuAtom";
import ChildMenuSideBar from "./ChildMenuSideBar";


const InfoRow = ({ label, value }) => (
    <div>
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800">{value ?? "—"}</p>
    </div>
);


const ChildMenuDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const logicContext = useContext(LogicContext);
    const myPromiseModal = createModal(GeneralModal);
    const printRef = useRef(null);
    const setChildMenuEdit = SetChildMenuEdit();

    const [current, setCurrent] = useState(null);

    const url = pathname.split("/");
    const currModuleId = url[1];
    const currModuleName = url[2];
    const currUserMenu = url[3];

    useEffect(() => {
        document.title = "Child Menu Details";
        if (!id) return;
        loadMenuDetails(id);
    }, [id]);

    const loadMenuDetails = async (menuId) => {
        try {
            const response = await moduleListService.getChildMenuDetails(menuId);
            if (response?.data) {
                setCurrent(response.data?.menu_child_info?.[0] ?? null);
            }
        } catch (err) {
            console.error(err);
            logicContext.showToast("Failed to load menu details", "error");
        }
    };

    const viewItemDetails = useCallback((itemId) => {
        console.log('menu_id', itemId);
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${itemId}`);
    }, [currModuleId, currModuleName, currUserMenu, navigate]);

    const isActive = current?.status_id === 'A' || current?.status_name === 'Active';
    const initials = current?.menu_name?.charAt(0)?.toUpperCase() || "C";

    const toggleStatus = async () => {
        const newStatus = isActive ? 'I' : 'A';
        const label = isActive ? 'Deactivate' : 'Activate';
        const confirmed = await myPromiseModal({
            title: `${label} Child Menu`,
            body: `Do you want to ${label.toLowerCase()} "${current?.menu_name}"?`,
            showInput: false,
        });
        if (!confirmed?.decision) return;

        try {
            const payload = {
                option: 2,
                menu_child_id: current.menu_id,
                menu_parent_id: current.menu_parent_id,
                menu_child_name: current.menu_name,
                menu_child_print_sequence: current.menu_parent_print_sequence,
                menu_child_status: newStatus,
            };
            const response = await moduleListService.saveChildMenu(payload);
            const result = logicContext.httpResponseManager(
                response.data?.[0] || response.data?.result_status?.[0]
            );
            if (result.message_id === 1) {
                setCurrent(prev => ({ ...prev, status_id: newStatus, status_name: newStatus === 'A' ? 'Active' : 'Inactive' }));
                logicContext.showToast(result.message_text || `Child Menu ${label.toLowerCase()}d successfully!`, "success");
            } else {
                logicContext.showToast(result.message_text || "Operation failed", "error");
            }
        } catch (err) {
            console.error(err);
            logicContext.showToast("Failed to update status", "error");
        }
    };

    const editCurrentData = async () => {
        const response = await myPromiseModal({
            title: "Modify Child Menu",
            body: "Do you want to modify this child menu?",
            showInput: false,
        });
        if (response.decision === true) {
            setChildMenuEdit(current);
            navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
        }
    };

    return (
        <section className="mt-4 w-full mx-auto px-4 sm:px-6 lg:px-8">

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mb-6">
                <button
                    onClick={editCurrentData}
                    className="p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition"
                    title="Edit"
                >
                    <img src={pencil} alt="Edit" className="w-5 h-5" />
                </button>
                <button
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition opacity-50 cursor-not-allowed"
                    title="Delete (not implemented)"
                    disabled
                >
                    <img src={trash} alt="Delete" className="w-5 h-5" />
                </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">

                {/* Sidebar */}
                <div className="flex-shrink-0">
                    <ChildMenuSideBar viewItemDetails={viewItemDetails} />
                </div>

                {/* Detail Card */}
                <div ref={printRef} className="flex-1">
                    <div className="bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100">

                        {/* Banner */}
                        <div className="h-28 bg-gradient-to-r from-blue-600 to-indigo-600 relative">
                            <div className="absolute -bottom-10 left-8 flex items-end gap-5">
                                <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                                    <div className="w-full h-full bg-blue-50 rounded-2xl flex items-center justify-center">
                                        <span className="text-3xl font-bold text-blue-600">{initials}</span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h1 className="text-xl font-bold text-white drop-shadow">
                                        {current?.menu_name || "Child Menu Details"}
                                    </h1>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white font-medium">
                                            ID: {current?.menu_id || "—"}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold border
                                            ${isActive
                                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                                : 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                                            {isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 pt-16">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                                {/* Child Menu Details */}
                                <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 space-y-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        Child Menu Details
                                    </h3>
                                    <InfoRow label="Menu ID" value={current?.menu_id} />
                                    <InfoRow label="Menu Name" value={current?.menu_name} />
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Status</p>
                                        <button
                                            onClick={toggleStatus}
                                            className="flex items-center gap-2.5 group"
                                            title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                        >
                                            <div className={`relative w-10 h-5 rounded-full transition-colors duration-200
                                                ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                                                    ${isActive ? 'translate-x-5' : 'translate-x-0'}`} />
                                            </div>
                                            <span className={`text-xs font-semibold ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                {isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Parent Menu */}
                                <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 space-y-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        Parent Menu
                                    </h3>
                                    <InfoRow label="Parent Menu ID" value={current?.menu_parent_id} />
                                    <InfoRow label="Parent Menu Name" value={current?.menu_parent_name} />
                                    <InfoRow label="Print Sequence" value={current?.menu_parent_print_sequence} />
                                </div>

                                {/* Module */}
                                <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 space-y-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        Module
                                    </h3>
                                    <InfoRow label="Module ID" value={current?.module_id} />
                                    <InfoRow label="Module Name" value={current?.module_name} />
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Module Tag</p>
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                            {current?.module_name || "—"}
                                        </span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChildMenuDetailsPage;
